import React from 'react';
import { Drawer, Box, Typography, IconButton, Divider, Chip, useTheme, useMediaQuery } from '@mui/material';
import { Icon } from '@iconify/react';
import DataTable, { Column } from './DataTable';
interface ReversalRecord {
  id: string;
  transactionId: string;
  mtoName: string;
  beneficiaryName: string;
  amount: string;
  currency: string;
  reversalReason: string;
  status: 'PENDING' | 'PROCESSED' | 'FAILED';
  processedAt?: string;
}
interface ReversalRecordsDrawerProps {
  open: boolean;
  onClose: () => void;
  uploadId: string;
  fileName: string;
  records: ReversalRecord[];
}
const ReversalRecordsDrawer: React.FC<ReversalRecordsDrawerProps> = ({
  open,
  onClose,
  uploadId,
  fileName,
  records
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const columns: Column<ReversalRecord>[] = [{
    id: 'id',
    label: 'Record ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-1691">
          {row.id}
        </Typography>
  }, {
    id: 'transactionId',
    label: 'Transaction ID',
    renderCell: row => <Typography variant="body2" fontWeight={600} fontFamily="monospace" noWrap data-id="element-1692">
          {row.transactionId}
        </Typography>
  }, {
    id: 'mtoName',
    label: 'MTO',
    renderCell: row => <Typography variant="body2" fontWeight={500} noWrap data-id="element-1693">
          {row.mtoName}
        </Typography>
  }, {
    id: 'beneficiaryName',
    label: 'Beneficiary',
    renderCell: row => <Typography variant="body2" noWrap data-id="element-1694">
          {row.beneficiaryName}
        </Typography>
  }, {
    id: 'amount',
    label: 'Amount',
    renderCell: row => <Typography variant="body2" fontWeight={600} noWrap data-id="element-1695">
          {row.currency} {row.amount}
        </Typography>
  }, {
    id: 'reversalReason',
    label: 'Reversal Reason',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap sx={{
      maxWidth: 200
    }} data-id="element-1696">
          {row.reversalReason}
        </Typography>
  }, {
    id: 'status',
    label: 'Status',
    renderCell: row => {
      const colors = {
        PENDING: {
          bg: 'rgba(59, 130, 246, 0.1)',
          color: '#3B82F6'
        },
        PROCESSED: {
          bg: 'rgba(16, 185, 129, 0.1)',
          color: '#10B981'
        },
        FAILED: {
          bg: 'rgba(239, 68, 68, 0.1)',
          color: '#EF4444'
        }
      };
      const style = colors[row.status];
      return <Chip label={row.status} size="small" sx={{
        backgroundColor: style.bg,
        color: style.color,
        fontWeight: 600,
        fontSize: '0.7rem'
      }} data-id="element-1697" />;
    }
  }, {
    id: 'processedAt',
    label: 'Processed At',
    renderCell: row => <Typography variant="body2" fontSize="0.8rem" noWrap data-id="element-1698">
          {row.processedAt ? new Date(row.processedAt).toLocaleString() : '-'}
        </Typography>
  }];
  return <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{
    sx: {
      width: isMobile ? '100%' : '90%',
      maxWidth: 1200
    }
  }} data-id="element-1699">
      <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%'
    }} data-id="element-1700">
        {/* Header */}
        <Box sx={{
        p: 3,
        pb: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start'
      }} data-id="element-1701">
          <Box data-id="element-1702">
            <Typography variant="h6" fontWeight={600} data-id="element-1703">
              Reversal Records
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{
            mt: 0.5
          }} data-id="element-1704">
              Upload ID: {uploadId}
            </Typography>
            <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mt: 1
          }} data-id="element-1705">
              <Icon icon="lucide:file-spreadsheet" width={16} height={16} color="#10B981" data-id="element-1706" />
              <Typography variant="body2" color="text.secondary" data-id="element-1707">
                {fileName}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" data-id="element-1708">
            <Icon icon="lucide:x" width={20} data-id="element-1709" />
          </IconButton>
        </Box>
        <Divider data-id="element-1710" />

        {/* Records Table */}
        <Box sx={{
        flex: 1,
        overflow: 'auto',
        p: 3
      }} data-id="element-1711">
          <DataTable title={`Reversal Records (${records.length})`} data={records} columns={columns} keyField="id" emptyStateMessage="No reversal records found" initialVisibleColumns={['id', 'transactionId', 'mtoName', 'beneficiaryName', 'amount', 'reversalReason', 'status', 'processedAt']} data-id="element-1712" />
        </Box>
      </Box>
    </Drawer>;
};
export default ReversalRecordsDrawer;